import { array } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import * as v from 'valibot';
import type Owner from '@ember/owner';

import TioLoadingMessages from 'tio-common/components/tio/loading-messages';
import Button from 'tio-ui/components/button';
import Form from 'tio-ui/components/form';
import Input from 'tio-ui/components/input';
import { VStack } from 'tio-ui/components/layout';
import type { FormResult } from 'tio-ui/components/form';
import type PostLoginService from 'tio-employee/services/post-login';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type RouterService from 'tio-employee/services/router';
import type SessionService from 'tio-employee/services/session';

interface AuthLoginLoginFormComponentSignature {
  Args: {
    /** Optional initial email value */
    email?: string;
  };
}

const formSchema = v.object({
  email: v.pipe(v.string(), v.nonEmpty('Email is required')),
  password: v.pipe(v.string(), v.nonEmpty('Password is required')),
});

type LoginFormData = v.InferOutput<typeof formSchema>;

export default class AuthLoginLoginFormComponent extends Component<AuthLoginLoginFormComponentSignature> {
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare preRegister: PreRegisterService;
  @service declare postLogin: PostLoginService;

  @tracked errorMessage = '';
  @tracked formData: LoginFormData;

  constructor(owner: Owner, args: AuthLoginLoginFormComponentSignature['Args']) {
    super(owner, args);
    this.formData = {
      email: args.email || '',
      password: '',
    };
  }

  validate = (data: LoginFormData) => {
    const r = v.safeParse(formSchema, data);
    return r.success ? {} : (v.flatten(r.issues).nested ?? {});
  };

  handleFormChange = (result: FormResult<LoginFormData>) => {
    this.formData = result.data;
  };

  handleFormSubmit = dropTask(async (result: FormResult<LoginFormData>) => {
    const { email, password } = result.data;

    this.router.detectRedirectParameter();

    try {
      await this.session.authenticate('authenticator:database', email, password);
      this.postLogin.initializeFromSession();
      if (this.postLogin.hasRequirements) {
        this.router.transitionTo(this.postLogin.nextRouteName ?? 'post-login');
      }
    } catch (error) {
      console.error(`Ember login error (${error.message}):`, error.details?.error ?? error.details);
      this.errorMessage = error.details?.error || error.message || 'Something went wrong.';
    }
  });

  <template>
    <Form
      @data={{this.formData}}
      @validate={{this.validate}}
      @onChange={{this.handleFormChange}}
      @onSubmit={{this.handleFormSubmit.perform}}
      as |form|
    >
      <VStack class="gap-y-6">
        {{#let (form.field "email") as |f|}}
          <Input
            @label={{t "common.email"}}
            @value={{f.value}}
            @onChange={{f.onChange}}
            @onBlur={{f.onBlur}}
            @errors={{f.errors}}
            data-test-email-input
          />
        {{/let}}

        {{#let (form.field "password") as |f|}}
          <Input
            @label={{t "common.password"}}
            @type="password"
            @value={{f.value}}
            @onChange={{f.onChange}}
            @onBlur={{f.onBlur}}
            @errors={{f.errors}}
            data-test-password-input
          />
        {{/let}}

        <div class="tio-flex-end">
          <a class="underline text-ocean-600" href="/forgot-password">
            {{t "login.forgot_password_question"}}
          </a>
        </div>

        {{#if this.handleFormSubmit.isRunning}}
          <div class="text-center">
            <TioLoadingMessages
              @i18nPath="login.loading_messages"
              @messageKeys={{array
                "verify_credentials"
                "wait_for_system_warmup"
                "thank_you_for_your_patience"
                "gather_account_details"
                "fetch_company_data"
              }}
            />
          </div>
        {{/if}}

        <div class="tio-flex-center">
          <Button
            @type="submit"
            @intent="primary"
            @isRunning={{this.handleFormSubmit.isRunning}}
            class="px-12"
            data-test-submit-button
          >
            {{t "login.form.submit"}}
          </Button>
        </div>
      </VStack>
    </Form>
    {{#if this.errorMessage}}
      <div class="mt-4 tio-fg-danger text-center">{{this.errorMessage}}</div>
    {{/if}}
  </template>
}
